import Category from 'types/category';

const DB_NAME = 'abrasoft_categories_db';
const DB_VERSION = 1;
const CATEGORIES_STORE = 'categories';
const PENDING_OPS_STORE = 'pending_category_ops';

export type PendingCategoryOperation =
  | {
      id: string;
      status: 'pending' | 'failed';
      type: 'create';
      payload: { name: string; parentId?: string };
      createdAt: number;
    }
  | {
      id: string;
      status: 'pending' | 'failed';
      type: 'update';
      payload: {
        categoryId: string;
        data: { name?: string; parentId?: string };
      };
      createdAt: number;
    }
  | {
      id: string;
      status: 'pending' | 'failed';
      type: 'delete';
      payload: { categoryId: string };
      createdAt: number;
    };

interface DBInstance {
  db: IDBDatabase | null;
  promise: Promise<IDBDatabase> | null;
}

const dbInstance: DBInstance = {
  db: null,
  promise: null,
};

function openDatabase(): Promise<IDBDatabase> {
  if (dbInstance.db) {
    return Promise.resolve(dbInstance.db);
  }

  if (dbInstance.promise) {
    return dbInstance.promise;
  }

  dbInstance.promise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      dbInstance.promise = null;
      reject(new Error('Failed to open categories IndexedDB'));
    };

    request.onsuccess = () => {
      dbInstance.db = request.result;
      dbInstance.promise = null;
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(CATEGORIES_STORE)) {
        db.createObjectStore(CATEGORIES_STORE, { keyPath: 'id' });
      }

      if (!db.objectStoreNames.contains(PENDING_OPS_STORE)) {
        db.createObjectStore(PENDING_OPS_STORE, { keyPath: 'id' });
      }
    };
  });

  return dbInstance.promise;
}

const generateId = (prefix: string) =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export async function cacheCategories(categories: { [key: string]: Category }) {
  const db = await openDatabase();
  const tx = db.transaction([CATEGORIES_STORE], 'readwrite');
  const store = tx.objectStore(CATEGORIES_STORE);

  // Clear old cache then add fresh records
  store.clear();
  Object.values(categories).forEach((category) => {
    store.put(category);
  });

  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(new Error('Failed to cache categories'));
  });
}

export async function getCachedCategories(): Promise<{
  [key: string]: Category;
}> {
  const db = await openDatabase();
  const tx = db.transaction([CATEGORIES_STORE], 'readonly');
  const store = tx.objectStore(CATEGORIES_STORE);

  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const result = request.result as Category[];
      const map: { [key: string]: Category } = {};
      result.forEach((cat) => {
        map[cat.id] = cat;
      });
      resolve(map);
    };
    request.onerror = () =>
      reject(new Error('Failed to read cached categories'));
  });
}

type PendingCategoryOperationInput =
  | {
      type: 'create';
      payload: { name: string; parentId?: string };
    }
  | {
      type: 'update';
      payload: {
        categoryId: string;
        data: { name?: string; parentId?: string };
      };
    }
  | {
      type: 'delete';
      payload: { categoryId: string };
    };

export async function addPendingCategoryOperation(
  operation: PendingCategoryOperationInput,
): Promise<PendingCategoryOperation> {
  const op: PendingCategoryOperation = {
    ...operation,
    id: generateId('cat_op'),
    createdAt: Date.now(),
    status: 'pending',
  };

  const db = await openDatabase();
  const tx = db.transaction([PENDING_OPS_STORE], 'readwrite');
  const store = tx.objectStore(PENDING_OPS_STORE);
  store.put(op);

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(op);
    tx.onerror = () =>
      reject(new Error('Failed to queue category operation for sync'));
  });
}

export async function getPendingCategoryOperations(): Promise<
  PendingCategoryOperation[]
> {
  const db = await openDatabase();
  const tx = db.transaction([PENDING_OPS_STORE], 'readonly');
  const store = tx.objectStore(PENDING_OPS_STORE);

  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const ops = (request.result as PendingCategoryOperation[]).sort(
        (a, b) => a.createdAt - b.createdAt,
      );
      resolve(ops);
    };
    request.onerror = () =>
      reject(new Error('Failed to read pending category operations'));
  });
}

export async function removePendingCategoryOperation(id: string) {
  const db = await openDatabase();
  const tx = db.transaction([PENDING_OPS_STORE], 'readwrite');
  tx.objectStore(PENDING_OPS_STORE).delete(id);

  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () =>
      reject(new Error('Failed to remove pending category operation'));
  });
}

export async function markCategoryOperationFailed(id: string) {
  const db = await openDatabase();
  const tx = db.transaction([PENDING_OPS_STORE], 'readwrite');
  const store = tx.objectStore(PENDING_OPS_STORE);
  const request = store.get(id);

  return new Promise<void>((resolve, reject) => {
    request.onsuccess = () => {
      const existing = request.result as PendingCategoryOperation;
      if (!existing) return reject(new Error('Category operation not found'));
      const updated = { ...existing, status: 'failed' }; // <-- fixed
      store.put(updated);
      resolve();
    };
    request.onerror = () =>
      reject(new Error('Failed to mark category operation as failed'));
  });
}
